import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useCompany } from '@/contexts/CompanyContext';

/**
 * Bridge component that syncs company/tenant changes to React Query cache
 * 
 * This component sits inside QueryProvider but under CompanyProvider,
 * allowing it to access both contexts. When the selected company changes,
 * it cancels in-flight queries and clears the cache to ensure 
 * fresh data is fetched for the new tenant.
 */
export function CompanyQuerySync({ children }: { children: React.ReactNode }) {
  const { selectedCompany } = useCompany();
  const queryClient = useQueryClient();
  const prevCompanyIdRef = useRef(selectedCompany?.id);

  useEffect(() => {
    // Only clear cache when company actually changes, not on initial mount
    if (prevCompanyIdRef.current !== selectedCompany?.id && prevCompanyIdRef.current !== undefined) {
      // Cancel all in-flight queries to abort their network requests
      queryClient.cancelQueries();
      
      // Clear the cache to force fresh fetches for the new tenant
      queryClient.clear();
    }
    
    prevCompanyIdRef.current = selectedCompany?.id;
  }, [selectedCompany?.id, queryClient]);

  return <>{children}</>;
}
